import React from "react";

import { AxiosResponse } from "axios";

import FilterByTopPanel, { Props as FilterByProps } from "./TopPanel/FilterBy";
import SearchSuggestTopPanel, { Props as SearchSuggestProps } from "./TopPanel/SearchSuggest";
import { colors } from "@zapalacorp/tokens";

export const Constants = {
  defaultPageSize: 20,
  defaultGeneralActionsIconSize: 18,
  defaultRowActionSize: 18,
  defaultColorIcon: "#475467",
  defaultColorBrand: colors.brandBlue500,
};

export interface Theme {
  primaryColor: string;
  hoverColor: string;
  pressedColor: string;
  disabledColor: string;
}

export const NewGridThemeKey = "ShipeeziNewGrid-Theme";

export interface GridProps {
  data: any[];
  loadingData?: boolean;
  columns: Column[];
  emptyData?: string | any;
  keyExtractor?: (dataObject: any, index?: number) => string;

  currentPage?: number;
  onNextPage?: Function;
  onPreviousPage?: Function;
  onGoToPage?: (page: number) => void;

  totalItems?: number;
  pageSize?: number;
  pageSizeList?: number[];
  onChangePageSize?: Function;

  namePage?: string;
  titlePage?: any;

  generalActions?: GeneralActions;
  rowActions?: RowActions;
  rowActionsExtra?: RowActionsExtra[];
  positionActionsColumn?: "left" | "right";
  /**
   * This property can be used if you do not want that render the action label of the last column
   */
  showActionLabel?: boolean;

  selecteds?: string[] | number[];
  handleSelect?: Function;
  clearSelection?: boolean;

  showSelection?: boolean;
  showHeaderGrid?: boolean;
  showFooterGrid?: boolean;
  showFooterPageInfo?: boolean;
  showFooterPaginationInfo?: boolean;
  showSort?: boolean;
  alwaysShowFooter?: boolean;

  onSorting?: Function;

  hasComponent?: boolean;
  component?: any;
  /**
   * Determines if the column chooser will be shown on the last column
   */
  showColumnChooser?: boolean;
  onChangeColumns?: (columns: string[]) => void;

  /**
   * This property can be used to render any element after the each row
   */
  renderAfterRow?: (dataObject: any, index?: number) => React.ReactNode;
  /**
   * This property can be used if you need send to the tr element of row, some style
   */
  rowStyle?: React.CSSProperties;
  /**
   * This property can be used if you need render sub rows children of the main row
   */
  insideData?: InsideData;
  /**
   * Makes the action column sticky and fixed
   */
  stickyActions?: boolean;

  gridCellPadding?: string;
  gridHeaderCellPadding?: string;

  style?: React.CSSProperties;
  footerStyle?: React.CSSProperties;
}

export interface Column {
  name: string;
  placeholderSearch?: string;
  title: string;
  searchIdentifier?: string;
  formatText?: (value?: any, isInsideRow?: boolean) => any;
  formatComponent?: (value?: any, row?: any, isInsideRow?: boolean) => any;
  show?: boolean;
  hideSort?: boolean;
  hideSearch?: boolean;
  associationColumnSettings?: {
    whenSearching: {
      disableSorting: (value: boolean) => void;
      clearSorting: (values: Array<Array<string>> | undefined) => void;
    };
    columnToSorting: string;
  };
  handleEdit?: (rowId: number, row: any, column: string) => void;
  /**
   * Removes column from shown column options
   */
  hideShowColumn?: boolean;
  pinned?: boolean;
  /**
   * Tooltip displayed next to the column name for its content information, only displayed if hideSort is true
   */
  info?: string;
  /**
   * If you dont need use text ellipsis, send to this property, the value 0 (number)
   */
  limitRowsToEllipsis?: number;
  /**
   * This property can be used to send some className to the column of row of table
   */
  className?: React.HtmlHTMLAttributes<unknown>["className"];
  /**
   * This property can be used to send some style to the column of row of table
   */
  style?: React.HtmlHTMLAttributes<unknown>["style"];
  /**
   * Overrides name in column chooser
   */
  key?: string;
  /**
   * Set column initial display to hidden
   */
  initialHideColumn?: boolean;
  /*
   * Set the column as sticky and give a width value from the actions column
   */
  stickyRight?: number;
}

export interface RowActions {
  orderBy?: string[];
  handleDetails?: (rowId: number | string, row: any) => void;
  handleEdit?: (rowId: number | string, row: any) => void;
  handlePrint?: (rowId: number | string, row: any) => void;
  handleDelete?: (rowId: number | string, row: any) => void;
  handleChange?: (rowId: number | string, row: any) => void;
  handleCommunication?: (rowId: number | string, row: any) => void;
}

export interface RowActionsExtra {
  title: string;
  button?: any;
  action?: (rowId: number | string, row: any) => void;
  buttonAndAction?: (rowId: number, row: any) => any;
  key?: string;
}

export interface Option {
  label: string;
  value: string;
}

export interface Category {
  name: string;
  column?: string;
  options?: string[] | Option[];
  optionsSelected?: string[];

  /**
   * This property will ignore the Category.options prop and will add the
   * AutoSuggest filter type to the Category
   */
  onSearch?: (value: string) => Promise<AxiosResponse<any>>;
  /**
   * Available only in AutoSuggest mode
   * Retrieves the selected property from onSearch result to be displayed in Chip
   */
  getOptionLabel?: (option: any) => string;
  /**
   * Available only in AutoSuggest mode
   * Allows the selection of multiple inputs inside AutoSugges
   * Defaults to false
   */
  multiple?: boolean;
  /**
   * Available only in AutoSuggest mode
   * Column from onSearch query result to be used in filter
   */
  optionColumn?: string;
}

export interface FilterBy {
  onChange?: (filters: { [x: string]: string[] }) => void;
  categories?: Category[];
}

export interface Search {
  onSearch?: (filters: Array<{ [x: string]: string }>, value: string) => void;
  inputPlaceholder?: string;
  inputSearchMode?: "onchange" | "enter";
  hideDefaultSearch?: boolean;
}

export interface DateOption {
  name: string;
  column: string;
  type?: DefaultDateOptionsColumn;
}

export interface ColumnFilter extends Column {
  value?: string;
}

export interface NewRecord {
  text: string;
  action?: () => void;
  tooltip?: string;
}

export interface OrderBy {
  left?: string[];
  right?: string[];
}

export interface DateSelect {
  dateOptions?: DateOption[];
  periodOptions?: DateOption[];
  startDate?: Date;
  endDate?: Date;
  onChange?: (
    filters: { [x: string]: string[] },
    dateType: string | undefined,
    periodType: string
  ) => void;
}

export interface GeneralActions {
  orderBy?: OrderBy;
  handleNewRecord?: NewRecord;
  handleSearch?: Search;
  handleFilterBy?: FilterBy;
  handlePrintAll?: (selectedList: any[]) => void;
  handleImport?: (selectedList: any[]) => void;
  handleExport?: (selectedList: any[]) => void;
  handleSendEmail?: (selectedList: any[]) => void;
  handleSelectDate?: DateSelect;
  [x: string]: any;
}

export enum SortType {
  ASC = "asc",
  DESC = "desc",
}

export interface SortColumn {
  columnName: string;
  direction: SortType;
  columnToSorting?: string;
}

export const SearchSuggest = (props: SearchSuggestProps) => SearchSuggestTopPanel(props);

export const Filters = (props: FilterByProps) => FilterByTopPanel(props);

/**
 * This interface represents the `insideData` object sent to this grid to enable render sub rows after each main row
 */
export interface InsideData {
  /**
   * This property need be use to get the array of rows inside the main row
   */
  dataKey: string;
  /**
   * This property can be used to check if the sub row will be show
   */
  show: ((data: any, mainRowIndex?: number) => boolean) | boolean;
  /**
   * This property can be used to render default row actions of the this sub row
   */
  rowActions?: GridProps["rowActions"];
  /**
   * This property can be used to render extra row actions of the this sub row
   */
  rowActionsExtra?: GridProps["rowActionsExtra"];
  /**
   * This property can be used if you need send to the tr element of row, some style
   */
  rowStyle?: GridProps["rowStyle"];
}

export interface ChangeStatusButtonProps extends React.HTMLAttributes<HTMLOrSVGElement> {
  divProps?: React.HTMLAttributes<HTMLDivElement>;
  color?: string;
  size?: number;
}

export enum DefaultDateOptionsColumn {
  DATE = "date",
  DATE_RANGE = "dateRange",
  HIDE_DATE_SELECTOR = "hideDateSelector",
}

export enum DefaultPeriodOptionsColumn {
  LAST_DAY = "lastDay",
  LAST_WEEK = "lastWeek",
  LAST_15_DAYS = "last15Days",
  LAST_MONTH = "lastMonth",
  CUSTOM_PERIOD = "customPeriod",
  CUSTOM_DATE = "customDate",
}
